//---------------------------------------------------------
// Soft — Linode accounts and SSH keys (admin and operator)
// One page: /soft/settings/linode. Keys are not nested under an account.
//---------------------------------------------------------
import {Box, Button} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
	SoftAlert,
	SoftCard,
	SoftIconAction,
	SoftInlineRow,
	SoftPage,
	SoftSection,
	SoftStack,
	SoftSubheading,
	SoftTable,
	SoftText,
	SoftTextField,
} from 'components/soft/SoftUI';
import {
	createLinodeAccount,
	createSSHKey,
	deleteLinodeAccount,
	deleteSSHKey,
	getCertExpiry,
	listLinodeAccounts,
	listSSHKeys,
} from 'connector/oam/softProvisionApi';
import {useRole} from 'hooks/query/oamHooks';
import {t} from 'i18next';
import {useCallback, useEffect, useState} from 'react';
import type {SoftLinodeAccount, SoftSSHKey} from 'types/soft_provision';
import {
	PRIVATE_KEY_ERROR,
	REMOVE_ACCOUNT_NOTE,
	REMOVE_KEY_NOTE,
	accountListFields,
	accountsStoredCopy,
	canUseLinodeSettings,
	rejectPrivateKeyPaste,
	sshKeyListFields,
} from './linodeUserKeys';
import {canSeeCertExpiryAlert, certExpiryAlert} from './certExpiryCopy';

type Section = 'accounts' | 'keys';

export default function LinodeSettingsPage() {
	const {role} = useRole();
	const [accounts, setAccounts] = useState<SoftLinodeAccount[]>([]);
	const [keys, setKeys] = useState<SoftSSHKey[]>([]);
	const [label, setLabel] = useState('');
	const [token, setToken] = useState('');
	const [keyLabel, setKeyLabel] = useState('');
	const [publicKey, setPublicKey] = useState('');
	// Page alerts render in the section they belong to.
	const [msg, setMsg] = useState<string | null>(null);
	const [err, setErr] = useState<string | null>(null);
	const [noticeAt, setNoticeAt] = useState<Section>('accounts');
	// Field-related error: shown on the Public key field, replacing its helper.
	const [keyFieldErr, setKeyFieldErr] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [expiry, setExpiry] = useState<{serverDaysLeft: number | null; caDaysLeft: number | null} | null>(null);

	const refresh = useCallback(async () => {
		const [accountList, keyList] = await Promise.all([listLinodeAccounts(), listSSHKeys()]);
		setAccounts(accountList);
		setKeys(keyList);
	}, []);

	useEffect(() => {
		if (canUseLinodeSettings(role)) refresh();
	}, [role, refresh]);

	// Cert-expiry alerts: who sees them is decided by canSeeCertExpiryAlert only.
	// The OAM route's 403 stays the control.
	const showCertAlerts = canSeeCertExpiryAlert(role);
	useEffect(() => {
		if (!showCertAlerts) {
			setExpiry(null);
			return;
		}
		let cancel = false;
		(async () => {
			const data = await getCertExpiry();
			if (!cancel) setExpiry(data);
		})();
		return () => {
			cancel = true;
		};
	}, [showCertAlerts]);

	if (role === null) return null;
	if (!canUseLinodeSettings(role)) {
		return (
			<SoftPage>
				<SoftAlert severity="error">{t('You need an admin or operator account for this page.')}</SoftAlert>
			</SoftPage>
		);
	}

	const onAdd = async () => {
		setNoticeAt('accounts');
		setBusy(true);
		setErr(null);
		setMsg(null);
		const res = await createLinodeAccount(label.trim(), token);
		setBusy(false);
		if (!res.ok) {
			setErr(res.error || 'save failed');
			return;
		}
		setToken('');
		setLabel('');
		setMsg(t('Account saved.'));
		await refresh();
	};

	const onDelete = async (id: string, accountLabel: string) => {
		setNoticeAt('accounts');
		setBusy(true);
		setErr(null);
		setMsg(null);
		const res = await deleteLinodeAccount(id);
		setBusy(false);
		if (!res.ok) {
			setErr(res.error || 'delete failed');
			return;
		}
		setMsg(t('Account removed') + `: ${accountLabel}`);
		await refresh();
	};

	const onAddKey = async () => {
		setNoticeAt('keys');
		const refused = rejectPrivateKeyPaste(publicKey);
		if (refused) {
			setKeyFieldErr(refused);
			setErr(null);
			setMsg(null);
			return;
		}
		setBusy(true);
		setErr(null);
		setMsg(null);
		setKeyFieldErr(null);
		const res = await createSSHKey(keyLabel.trim(), publicKey.trim());
		setBusy(false);
		if (!res.ok) {
			if (res.error === PRIVATE_KEY_ERROR) setKeyFieldErr(PRIVATE_KEY_ERROR);
			else setErr(res.error || 'save failed');
			return;
		}
		setPublicKey('');
		setKeyLabel('');
		setMsg(t('SSH key saved.'));
		await refresh();
	};

	const onDeleteKey = async (id: string, savedLabel: string) => {
		setNoticeAt('keys');
		setBusy(true);
		setErr(null);
		setMsg(null);
		const res = await deleteSSHKey(id);
		setBusy(false);
		if (!res.ok) {
			setErr(res.error || 'delete failed');
			return;
		}
		setMsg(t('SSH key removed') + `: ${savedLabel}`);
		await refresh();
	};

	const notices = (section: Section) =>
		noticeAt === section ? (
			<>
				{msg && <SoftAlert severity="success">{msg}</SoftAlert>}
				{err && <SoftAlert severity="error">{err}</SoftAlert>}
			</>
		) : null;

	const serverAlert = showCertAlerts ? certExpiryAlert('server', expiry?.serverDaysLeft ?? null) : null;
	const caAlert = showCertAlerts ? certExpiryAlert('ca', expiry?.caDaysLeft ?? null) : null;

	return (
		<SoftPage title={t('Linode')}>
			<Box maxWidth={960}>
				{serverAlert && <SoftAlert severity={serverAlert.severity}>{t(serverAlert.text)}</SoftAlert>}
				{caAlert && <SoftAlert severity={caAlert.severity}>{t(caAlert.text)}</SoftAlert>}
				<SoftSection title={t('Accounts')}>
					<Box>
						<SoftText>{accountsStoredCopy(accounts.length)}</SoftText>
						<SoftText>{t('Accounts used to provision a load balancer. A saved token shows only its last four characters.')}</SoftText>
						<SoftText>{REMOVE_ACCOUNT_NOTE}</SoftText>
					</Box>
					<SoftTable<SoftLinodeAccount>
						label={t('Accounts')}
						rows={accounts}
						rowKey={a => a.id}
						empty={t('No Linode accounts configured')}
						columns={[
							{key: 'label', header: t('Label'), render: a => accountListFields(a).label},
							{
								key: 'token',
								header: t('Token'),
								mono: true,
								render: a => (accountListFields(a).tokenLast4 ? `…${accountListFields(a).tokenLast4}` : ''),
							},
							{key: 'user', header: t('Saved by'), muted: true, render: a => accountListFields(a).username},
							{
								key: 'updated',
								header: t('Updated'),
								muted: true,
								render: a => (a.updatedAt ? new Date(a.updatedAt).toLocaleString() : ''),
							},
							{
								key: 'actions',
								header: '',
								align: 'right',
								width: 64,
								render: a => (
									<SoftIconAction
										label={t('Remove')}
										icon={<DeleteOutlineIcon />}
										destructive
										disabled={busy}
										onClick={() => onDelete(a.id, accountListFields(a).label)}
									/>
								),
							},
						]}
					/>
					{notices('accounts')}
					<SoftCard>
						<SoftStack>
							<SoftSubheading>{t('Add account')}</SoftSubheading>
							<SoftInlineRow
								action={
									<Button variant="contained" disabled={busy || !label.trim() || !token.trim()} onClick={onAdd}>
										{t('Add')}
									</Button>
								}
							>
								<SoftTextField
									id="soft-linode-account-label"
									label={t('Label')}
									value={label}
									onChange={e => setLabel(e.target.value)}
									autoComplete="off"
									helper={t('Display name for this Linode account (e.g. Primary)')}
								/>
								<SoftTextField
									id="soft-linode-account-token"
									label={t('Linode API token')}
									type="password"
									value={token}
									onChange={e => setToken(e.target.value)}
									autoComplete="off"
									helper={t('Paste the token once. Only the last four characters are shown after it is saved.')}
								/>
							</SoftInlineRow>
						</SoftStack>
					</SoftCard>
				</SoftSection>

				<SoftSection title={t('SSH keys')}>
					<Box>
						<SoftText>{t('A saved key shows its label and who saved it. The key itself is not shown again.')}</SoftText>
						<SoftText>{REMOVE_KEY_NOTE}</SoftText>
					</Box>
					<SoftTable<SoftSSHKey>
						label={t('SSH keys')}
						rows={keys}
						rowKey={k => k.id}
						empty={t('No SSH keys configured')}
						columns={[
							{key: 'label', header: t('Label'), render: k => sshKeyListFields(k).label},
							{key: 'user', header: t('Saved by'), muted: true, render: k => sshKeyListFields(k).username},
							{
								key: 'actions',
								header: '',
								align: 'right',
								width: 64,
								render: k => (
									<SoftIconAction
										label={t('Remove')}
										icon={<DeleteOutlineIcon />}
										destructive
										disabled={busy}
										onClick={() => onDeleteKey(k.id, sshKeyListFields(k).label)}
									/>
								),
							},
						]}
					/>
					{notices('keys')}
					<SoftCard>
						<SoftStack>
							<SoftSubheading>{t('Add SSH key')}</SoftSubheading>
							<SoftInlineRow
								action={
									<Button variant="contained" disabled={busy || !keyLabel.trim() || !publicKey.trim()} onClick={onAddKey}>
										{t('Add')}
									</Button>
								}
							>
								<SoftTextField
									id="soft-linode-key-label"
									label={t('Label')}
									value={keyLabel}
									onChange={e => setKeyLabel(e.target.value)}
									autoComplete="off"
								/>
								<SoftTextField
									id="soft-linode-key-public"
									label={t('Public key')}
									value={publicKey}
									onChange={e => {
										setPublicKey(e.target.value);
										if (keyFieldErr) setKeyFieldErr(null);
									}}
									multiline
									minRows={3}
									autoComplete="off"
									helper={t('Paste a public key. A private key is refused and is not stored.')}
									error={keyFieldErr}
								/>
							</SoftInlineRow>
						</SoftStack>
					</SoftCard>
				</SoftSection>
			</Box>
		</SoftPage>
	);
}
